/**
 * detectUploads.js — PROD-1198 and PROD-1199 (Alex, 2026-10-19). THE ONE OWNER of "which card does
 * an upload belong to" and of "what Detect saves". No imports, so the node gate can load it.
 *
 * ★ PROD-1198: a photo picked from a suggestion card went through the same file input as the plain
 *   Photos tile and carried no link to the card, so it showed only in the general uploads list and
 *   the patient could not tell the card had worked. A file picked from a card now carries the card:
 *     file.suggestion   'capture:<label>'  an AI capture card ("Suggested for your case")
 *                       'record:<label>'   an AI prior-record card ("Tap to attach")
 *     file.priorRecord  '<row key>'        a pinned prior-test row (utils/priorTests.js, s107)
 *   The card renders its own files, and the general list leaves them out. A file whose card is not
 *   on screen (the guidance changed, the answer was edited) stays in the general list, so no upload
 *   is ever hidden.
 *
 * ★ PROD-1199: "Nothing to add" could be ticked with files present, and the save then wrote both.
 *   The option shows only while there are no files, and the save never sends files together with
 *   noFilesToAdd = true.
 */

const KINDS = ['photos', 'videos', 'documents']
const listOf = (uploads, kind) => (uploads && Array.isArray(uploads[kind]) ? uploads[kind] : [])

export const captureCardKey = (label) => 'capture:' + label
export const recordCardKey = (label) => 'record:' + label
export const pinnedCardKey = (rowKey) => 'pin:' + rowKey

/** The card key a file was picked from, or null for a file from the general uploaders. */
export function cardKeyOf(file) {
  if (!file || typeof file !== 'object') return null
  if (typeof file.suggestion === 'string' && file.suggestion) return file.suggestion
  if (typeof file.priorRecord === 'string' && file.priorRecord) return pinnedCardKey(file.priorRecord)
  return null
}

/** Total number of files in the three lists. */
export const uploadCount = (uploads) => KINDS.reduce((n, k) => n + listOf(uploads, k).length, 0)

/**
 * Split the uploads into the files each card on screen owns and the general list.
 * @param uploads  { photos, videos, documents }
 * @param cardKeys the keys of the cards actually rendered
 * @returns { byCard: Map<key, [{ file, kind }]>, general: { photos, videos, documents } }
 */
export function splitUploads(uploads, cardKeys) {
  const shown = new Set(Array.isArray(cardKeys) ? cardKeys : [])
  const byCard = new Map()
  const general = { photos: [], videos: [], documents: [] }
  for (const kind of KINDS) {
    for (const file of listOf(uploads, kind)) {
      const key = cardKeyOf(file)
      if (key && shown.has(key)) {
        if (!byCard.has(key)) byCard.set(key, [])
        byCard.get(key).push({ file, kind })
      } else {
        general[kind].push(file)
      }
    }
  }
  return { byCard, general }
}

/** PROD-1199: the "Nothing to add" option exists only while there is nothing added. */
export const showNothingToAdd = (uploads) => uploadCount(uploads) === 0

/** PROD-1199: the detect object Detect saves. Files and noFilesToAdd = true never go together. */
export function detectSaveData(uploads, noFilesToAdd) {
  const photos = listOf(uploads, 'photos')
  const videos = listOf(uploads, 'videos')
  const documents = listOf(uploads, 'documents')
  const none = photos.length + videos.length + documents.length === 0
  return { photos, videos, documents, noFilesToAdd: none && noFilesToAdd === true, completed: true }
}
